/**
 * Event Force Staffing Services - Brand Color Palette
 * Professional theme aligned with company branding
 */

export const brandColors = {
  // Primary Brand Colors
  primary: {
    navy: '#1B3B6F',        // Main brand navy blue
    navyLight: '#2A4A7F',   // Lighter navy for hovers
    navyDark: '#0F2850',    // Darker navy for depth
  },
  
  // Secondary Colors
  secondary: {
    blueGray: '#6B7C93',    // Secondary blue-gray
    tan: '#9B926B',         // Accent tan/gold
    yellow: '#F4C430',      // CTA yellow
    yellowLight: '#F7D65A', // Lighter yellow for hovers
  },
  
  // Neutral Grays - Professional palette
  gray: {
    50: '#F8F9FA',   // Lightest - backgrounds
    100: '#F1F3F5',  // Very light - secondary backgrounds
    200: '#E9ECEF',  // Light - borders, dividers
    300: '#DEE2E6',  // Medium light - disabled states
    400: '#CED4DA',  // Medium - placeholder text
    500: '#ADB5BD',  // Medium dark - secondary text
    600: '#6C757D',  // Dark - body text
    700: '#495057',  // Darker - headings
    800: '#343A40',  // Very dark - primary text
    900: '#212529',  // Darkest - emphasis
  },
  
  // Status Colors
  status: {
    success: '#28A745',     // Green for success states
    successLight: '#D4EDDA',
    warning: '#FFC107',     // Warning states
    warningLight: '#FFF3CD',
    error: '#DC3545',       // Error states
    errorLight: '#F8D7DA',
    info: '#17A2B8',        // Info states
    infoLight: '#D1ECF1',
  },
  
  // Functional Colors
  functional: {
    white: '#FFFFFF',
    black: '#000000',
    focus: '#0377FC',       // Focus ring (keep accessibility)
    focusRing: 'rgba(3, 119, 252, 0.2)',
  }
};

// Component-specific color schemes
export const componentThemes = {
  // Signature Canvas Theme
  signature: {
    background: brandColors.functional.white,
    border: brandColors.gray[300],
    borderHover: brandColors.primary.navy,
    borderFocus: brandColors.primary.navy,
    focusRing: 'rgba(27, 59, 111, 0.2)',
    buttonPrimary: brandColors.secondary.yellow,
    buttonPrimaryHover: brandColors.secondary.yellowLight,
    buttonSecondary: brandColors.gray[100],
    buttonSecondaryHover: brandColors.gray[200],
    buttonDanger: brandColors.status.error,
    text: brandColors.gray[800],
  },
  
  // Form Fields Theme
  form: {
    background: brandColors.functional.white,
    backgroundDisabled: brandColors.gray[100],
    border: brandColors.gray[300],
    borderHover: brandColors.gray[400],
    borderFocus: brandColors.primary.navy,
    borderError: brandColors.status.error,
    focusRing: 'rgba(27, 59, 111, 0.2)',
    label: brandColors.gray[700],
    labelRequired: brandColors.secondary.yellow,
    text: brandColors.gray[800],
    placeholder: brandColors.gray[400],
  },
  
  // Navigation/Sidebar Theme
  navigation: {
    background: brandColors.gray[50],
    itemBackground: brandColors.functional.white,
    itemBackgroundActive: brandColors.primary.navy,
    itemBackgroundHover: brandColors.gray[100],
    text: brandColors.gray[700],
    textActive: brandColors.functional.white,
    checkmark: brandColors.status.success,
    border: brandColors.gray[200],
  },
  
  // Header/Section Theme
  header: {
    background: brandColors.primary.navy,
    backgroundGradient: `linear-gradient(135deg, ${brandColors.primary.navy} 0%, ${brandColors.primary.navyLight} 100%)`,
    text: brandColors.functional.white,
    accent: brandColors.secondary.yellow,
  },
  
  // Button Themes
  button: {
    primary: {
      background: brandColors.secondary.yellow,
      backgroundHover: brandColors.secondary.yellowLight,
      text: brandColors.primary.navy,
      border: brandColors.secondary.yellow,
    },
    secondary: {
      background: brandColors.primary.navy,
      backgroundHover: brandColors.primary.navyLight,
      text: brandColors.functional.white,
      border: brandColors.primary.navy,
    },
    outline: {
      background: 'transparent',
      backgroundHover: brandColors.gray[50],
      text: brandColors.primary.navy,
      border: brandColors.primary.navy,
    },
    ghost: {
      background: 'transparent',
      backgroundHover: brandColors.gray[100],
      text: brandColors.gray[700],
      border: 'transparent',
    },
    danger: {
      background: brandColors.status.error,
      backgroundHover: '#C82333',
      text: brandColors.functional.white,
      border: brandColors.status.error,
    },
  },
  
  // Card/Panel Theme
  card: {
    background: brandColors.functional.white,
    border: brandColors.gray[200],
    shadow: '0 2px 8px rgba(27, 59, 111, 0.08)',
    shadowHover: '0 4px 16px rgba(27, 59, 111, 0.12)',
  },
  
  // Signature Library Theme
  signatureLibrary: {
    modalBackground: 'rgba(27, 59, 111, 0.75)',
    modalContent: brandColors.functional.white,
    headerBackground: brandColors.primary.navy,
    headerText: brandColors.functional.white,
    importButton: brandColors.secondary.yellow,
    importButtonHover: brandColors.secondary.yellowLight,
    importButtonText: brandColors.primary.navy,
    useButton: brandColors.secondary.tan,
    useButtonHover: '#8A8160',
    deleteButton: brandColors.status.error,
    deleteButtonHover: '#C82333',
    itemBorder: brandColors.gray[200],
    itemBackground: brandColors.gray[50],
    emptyStateText: brandColors.gray[500],
  }
};

// Spacing system
export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  xxl: '3rem',     // 48px
  xxxl: '4rem',    // 64px
};

// Border radius system
export const borderRadius = {
  sm: '0.25rem',   // 4px
  md: '0.375rem',  // 6px
  lg: '0.5rem',    // 8px
  xl: '0.75rem',   // 12px
  full: '9999px',  // Fully rounded
};

// Typography
export const typography = {
  fontFamily: {
    base: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem',// 30px
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

export default {
  colors: brandColors,
  components: componentThemes,
  spacing,
  borderRadius,
  typography,
};
